import { formatGender, formatNameLastFirst } from '@strata/helper-functions';
import { Dialog } from '@strata/react-dialog';
import { FormSectionRow } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { AddressDetailLabelValue, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import PropTypes from 'prop-types';

const Statuses = {
    ELIGIBLE: 'eligible',
    INELIGIBLE: 'ineligible',
    PATIENT_NOT_FOUND: 'patient-not-found',
};

const getDialogStatus = (benefitInfo) => {
    if (!benefitInfo) {
        return Statuses.PATIENT_NOT_FOUND;
    }
    return benefitInfo.is_eligible ? Statuses.ELIGIBLE : Statuses.INELIGIBLE;
};

const DialogTitle = {
    [Statuses.ELIGIBLE]: 'Confirm Patient',
    [Statuses.INELIGIBLE]: 'Ineligible Patient',
    [Statuses.PATIENT_NOT_FOUND]: 'Patient Not Found',
};

const PrimaryButtonLabel = {
    [Statuses.ELIGIBLE]: 'Confirm Patient',
    [Statuses.INELIGIBLE]: 'Cancel Referral',
    [Statuses.PATIENT_NOT_FOUND]: 'Cancel Referral',
};

const DialogInlineMessage = {
    [Statuses.ELIGIBLE]: (
        <InlineMessage intent={InlineMessage.Intent.SUCCESS} label="Patient is eligible." />
    ),
    [Statuses.INELIGIBLE]: (
        <InlineMessage intent={InlineMessage.Intent.ERROR} label="Patient is not eligible." />
    ),
    [Statuses.PATIENT_NOT_FOUND]: (
        <InlineMessage
            intent={InlineMessage.Intent.ERROR}
            label="Unable to verify benefits and eligibility."
        />
    ),
};

export const EligibilityDialog = ({
    benefitInfo = undefined,
    isOpen = false,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
}) => {
    const dialogStatus = getDialogStatus(benefitInfo);
    return (
        <Dialog
            dialogTitle={DialogTitle[dialogStatus]}
            primaryButtonLabel={PrimaryButtonLabel[dialogStatus]}
            secondaryButtonLabel="Return to Form"
            isOpen={isOpen}
            onPrimaryButtonClick={onPrimaryButtonClick}
            onSecondaryButtonClick={onSecondaryButtonClick}
            onClose={onSecondaryButtonClick}
            dialogContent={
                <>
                    <div className="margin__bottom--large">{DialogInlineMessage[dialogStatus]}</div>
                    {dialogStatus === Statuses.PATIENT_NOT_FOUND ? (
                        <div>
                            If you have entered incorrect information, please return to the form and
                            correct it.
                        </div>
                    ) : (
                        <>
                            <FormSectionRow
                                rowContent={
                                    <>
                                        <DetailLabelValuePair
                                            label="Patient Name"
                                            value={formatNameLastFirst(
                                                benefitInfo.patient_last_name,
                                                benefitInfo.patient_first_name,
                                            )}
                                        />
                                        <DetailLabelValuePair
                                            label="Age"
                                            value={benefitInfo.member_age}
                                        />
                                        <DetailLabelValuePair
                                            label="Gender"
                                            value={formatGender(benefitInfo.gender)}
                                        />
                                    </>
                                }
                            />
                            <FormSectionRow
                                rowContent={
                                    <AddressDetailLabelValue
                                        address1={benefitInfo.patient_address_line_1}
                                        address2={benefitInfo.patient_address_line_2}
                                        city={benefitInfo.member_city}
                                        state={benefitInfo.member_state}
                                        zip={benefitInfo.member_zip}
                                    />
                                }
                            />
                        </>
                    )}
                </>
            }
        />
    );
};

EligibilityDialog.propTypes = {
    benefitInfo: PropTypes.shape({
        is_eligible: PropTypes.bool,
        patient_first_name: PropTypes.string,
        patient_last_name: PropTypes.string,
        member_age: PropTypes.number,
        gender: PropTypes.string,
        patient_address_line_1: PropTypes.string,
        patient_address_line_2: PropTypes.string,
        member_city: PropTypes.string,
        member_state: PropTypes.string,
        member_zip: PropTypes.string,
    }),
    isOpen: PropTypes.bool,
    onPrimaryButtonClick: PropTypes.func.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
};
